/**
 * Internationalization (i18n) Configuration
 * 
 * This module defines the supported locales, default settings, and
 * locale-related utilities for the Nordex platform.
 */

// Supported locales in the format: language-REGION
export const SUPPORTED_LOCALES = ['en-US', 'ar-AE', 'da-DK'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

// Default locale used when no preference is set or detected
export const DEFAULT_LOCALE: SupportedLocale = 'en-US';

// Locales that use right-to-left text direction
export const RTL_LOCALES: SupportedLocale[] = ['ar-AE'];

// Human-readable names for each locale (in their native language)
export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  'en-US': 'English',
  'ar-AE': 'العربية',
  'da-DK': 'Dansk',
};

// ISO 639-1 language codes for each locale (for HTML lang attribute)
export const LOCALE_LANGUAGES: Record<SupportedLocale, string> = {
  'en-US': 'en',
  'ar-AE': 'ar',
  'da-DK': 'da',
};

// Country flags (as emoji) for each locale
export const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  'en-US': '🇺🇸',
  'ar-AE': '🇦🇪',
  'da-DK': '🇩🇰',
};

/**
 * Check if a locale uses right-to-left text direction
 */
export function isRTL(locale: string): boolean {
  return RTL_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Check if a locale is supported
 */
export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Map browser language to supported locale
 * 
 * Examples:
 * - "ar" -> "ar-AE"
 * - "ar-SA" -> "ar-AE"  (Arabic from Saudi Arabia falls back to ar-AE)
 * - "en" -> "en-US"
 * - "en-GB" -> "en-US"  (British English falls back to en-US)
 * - "de" -> "en-US"     (German falls back to default)
 */
export function mapBrowserLangToLocale(browserLang: string): SupportedLocale {
  // Normalize to lowercase
  const normalized = browserLang.toLowerCase();
  
  // First try exact match
  for (const locale of SUPPORTED_LOCALES) {
    if (normalized === locale.toLowerCase()) {
      return locale;
    }
  }
  
  // Then try language prefix match (e.g., "ar" matches "ar-AE")
  const langPrefix = normalized.split('-')[0];
  for (const locale of SUPPORTED_LOCALES) {
    if (locale.toLowerCase().startsWith(langPrefix + '-')) {
      return locale;
    }
  }
  
  // Fall back to default
  return DEFAULT_LOCALE;
}

/**
 * Get the text direction for a locale
 */
export function getTextDirection(locale: string): 'ltr' | 'rtl' {
  return isRTL(locale) ? 'rtl' : 'ltr';
}
